import React, { ChangeEvent, ReactElement, useEffect, useState } from "react";
import { IMillProductDetail, IRowData } from "../types";
import {
  CircularProgress,
  Paper,
  Table, TableBody,
  TableCell,
  TableHead, TablePagination,
  TableRow,
  TableSortLabel
} from "@mui/material";
import {
  formatDateTimeWithSeconds,
  getComparator,
  stableSort, toTons
} from "../../../system/utils";
import { IColumn, IParentSize, Order } from "../../../types";
import { DateTime } from "luxon";
import ProductionTableGroupedRow from "./productionTableGroupedRow";
import TableContainer  from "../../../components/tableContainer";
import TablePaginationActions from "../../../components/tablePaginationActions";

interface IProductDetailProps {
  products: IMillProductDetail[] | null;
  parentSize: IParentSize;
}

const columns: IColumn[] = [
  {
    id: "beginDatetime",
    label: "Begin Datetime",
    minWidth: 180,
    format: value => formatDateTimeWithSeconds(value),
  },
  {
    id: "endDatetime",
    label: "End Datetime",
    minWidth: 180,
    format: value => formatDateTimeWithSeconds(value),
  },
  {
    id: "totalBillets",
    label: "Total Billets",
    minWidth: 150,
    align: "right",
  },
  {
    id: "totalWeight",
    label: <span>Total Weight <sub>[US ton]</sub></span>,
    minWidth: 200,
    align: "right",
    format: value => toTons(value).toFixed(2)
  }
];

const ProductionTableGrouped = ({ products, parentSize }: IProductDetailProps): ReactElement => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof IRowData>("beginDatetime");
  const [selectedItem, setSelectedItem] = useState<IRowData | null>(null);
  const [dataSet, setDataSet] = useState<IRowData[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const tableContainerHeight = parentSize.height - 50;

  const dataLength = dataSet ? dataSet.length : 0;
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dataLength) : 0;

  useEffect(() => {
    if(!products) {
      setDataSet([]);

      return;
    }

    const data: IRowData[] = [];

    products.forEach(detail => {

      let record = data.find(x => detail.beginDatetime >= x.beginDatetime && detail.beginDatetime < x.endDatetime);
      if(!record) {

        const beginDatetime = DateTime.fromSeconds(detail.beginDatetime).set({ minute: 0, second: 0 });
        const endDatetime = beginDatetime.plus({ hour: 1 });

        record = {
          beginDatetime: beginDatetime.toSeconds(),
          endDatetime: endDatetime.toSeconds(),
          totalBillets: 1,
          totalWeight: detail.weight ?? 0,
          machineDetail: [
            {
              name: detail.machine.name,
              totalBillets: 1,
              totalWeight: detail.weight ?? 0,
              products: [ detail ]
            }
          ]
        };

        data.push(record);
      } else {
        record.totalBillets += 1;
        record.totalWeight += detail.weight ?? 0;

        const machine = record.machineDetail.find(x => detail.machine.name === x.name);
        if (!machine) {
          record.machineDetail.push({
            name: detail.machine.name,
            totalBillets: 1,
            totalWeight: detail.weight ?? 0,
            products: [ detail ]
          });
        } else {
          machine.totalBillets += 1;
          machine.totalWeight += detail.weight ?? 0;
          machine.products.push(detail);
        }
      }
    });

    setDataSet(data);
  }, [products]);

  const createSortHandler = (property: keyof IRowData) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number,) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  return (
    <>
      <TableContainer style={{ height: tableContainerHeight, maxHeight: tableContainerHeight }}>
        <Table stickyHeader={true} size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell className="table-header" />
              {columns.map((column) =>
                column.id !== ""
                  ? <TableCell
                    className="table-header"
                    key={column.id}
                    align={column.align}
                    width={column.minWidth}
                    style={{ minWidth: column.minWidth }}
                    sortDirection={orderBy === column.id ? order : false}>
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={createSortHandler(column.id as keyof IRowData)}>
                      {column.label}
                      {orderBy === column.id
                        ? <span className="cell-visuallyHidden">
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </span>
                        : null}
                    </TableSortLabel>
                  </TableCell>
                  : <TableCell className="table-header" style={{ width: 20 }}/>
              )}
              <TableCell className="table-header"/>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataSet && stableSort<IRowData>(dataSet, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index: number) => {
                return (
                  <ProductionTableGroupedRow key={`alarm-row-${index}`} row={row} selectedItem={selectedItem} setSelectedItem={setSelectedItem} index={index}/>
                );
              })
            }
            {emptyRows > 0 &&
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6}/>
              </TableRow>
            }

          </TableBody>
        </Table>
        {!dataSet &&
          <Paper className="display-flex-row justify-content-center align-items-center w-100 p-2"  >
            <CircularProgress className=""/>
          </Paper>
        }
      </TableContainer>
      <Table>
        <TableBody>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, { label: "All", value: -1 }]}
              colSpan={columns.length + 2}
              count={dataLength}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableBody>
      </Table>

    </>
  );
};

export default ProductionTableGrouped;
